/**
 * VISTOERA_SAAS_I18N_FOUNDATION V1 — i18next instance configuration.
 *
 * TECH SELECTION (final report §I18N_LIBRARY): `i18next` + `react-i18next`.
 * No canonical i18n framework existed in this repo before this round (see
 * the audit in the final report) — this is a fresh, deliberate pick, not
 * a second system layered on an existing one. Rationale: mature, typed,
 * first-class React bindings, built-in interpolation + ICU-less plural
 * rules, namespace support, a documented `missingKeyHandler` for dev
 * warnings (spec §20), and a well-understood lazy-namespace-loading path
 * (`i18next-http-backend` / dynamic `addResourceBundle`) for whenever V1's
 * "just bundle both locales" (spec §38) needs to change.
 *
 * BUNDLING (spec §38/§39): V1 ships exactly two locales, statically
 * imported below — no runtime `fetch()`, no per-namespace network request,
 * no DB-backed translation lookup. Every `t()` call is a synchronous,
 * already-in-memory object read. Measured cost — see
 * `I18N_BUNDLE_DELTA_KB` in the final report.
 */
import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import { DEFAULT_UI_LOCALE, FALLBACK_UI_LOCALE, SUPPORTED_UI_LOCALES } from './types'
import { resolveInitialUiLocale } from './localeStorage'

// zh-CN
import zhCommon from './locales/zh-CN/common.json'
import zhNavigation from './locales/zh-CN/navigation.json'
import zhActions from './locales/zh-CN/actions.json'
import zhStatus from './locales/zh-CN/status.json'
import zhContentProjects from './locales/zh-CN/contentProjects.json'
import zhImageAssets from './locales/zh-CN/imageAssets.json'
import zhCanvas from './locales/zh-CN/canvas.json'
import zhValidation from './locales/zh-CN/validation.json'
import zhWebsite from './locales/zh-CN/website.json'
import zhPlatformAdmin from './locales/zh-CN/platformAdmin.json'
import zhAutoProcess from './locales/zh-CN/autoProcess.json'
import zhAccountEntry from './locales/zh-CN/accountEntry.json'
import zhMembership from './locales/zh-CN/membership.json'
import zhLegal from './locales/zh-CN/legal.json'
import zhLocalPublisher from './locales/zh-CN/localPublisher.json'
import zhVideo from './locales/zh-CN/video.json'
import zhOverview from './locales/zh-CN/overview.json'
import zhAiChat from './locales/zh-CN/aiChat.json'
import zhVirtualIp from './locales/zh-CN/virtualIp.json'
import zhImageProcessing from './locales/zh-CN/imageProcessing.json'
import zhCompetitiveAnalysis from './locales/zh-CN/competitiveAnalysis.json'
import zhPublishing from './locales/zh-CN/publishing.json'
import zhMarketingAnalytics from './locales/zh-CN/marketingAnalytics.json'
import zhTextAssets from './locales/zh-CN/textAssets.json'
import zhAudioAssets from './locales/zh-CN/audioAssets.json'
import zhTeam from './locales/zh-CN/team.json'

// en-US
import enCommon from './locales/en-US/common.json'
import enNavigation from './locales/en-US/navigation.json'
import enActions from './locales/en-US/actions.json'
import enStatus from './locales/en-US/status.json'
import enContentProjects from './locales/en-US/contentProjects.json'
import enImageAssets from './locales/en-US/imageAssets.json'
import enCanvas from './locales/en-US/canvas.json'
import enValidation from './locales/en-US/validation.json'
import enWebsite from './locales/en-US/website.json'
import enPlatformAdmin from './locales/en-US/platformAdmin.json'
import enAutoProcess from './locales/en-US/autoProcess.json'
import enAccountEntry from './locales/en-US/accountEntry.json'
import enMembership from './locales/en-US/membership.json'
import enLegal from './locales/en-US/legal.json'
import enLocalPublisher from './locales/en-US/localPublisher.json'
import enVideo from './locales/en-US/video.json'
import enOverview from './locales/en-US/overview.json'
import enAiChat from './locales/en-US/aiChat.json'
import enVirtualIp from './locales/en-US/virtualIp.json'
import enImageProcessing from './locales/en-US/imageProcessing.json'
import enCompetitiveAnalysis from './locales/en-US/competitiveAnalysis.json'
import enPublishing from './locales/en-US/publishing.json'
import enMarketingAnalytics from './locales/en-US/marketingAnalytics.json'
import enTextAssets from './locales/en-US/textAssets.json'
import enAudioAssets from './locales/en-US/audioAssets.json'
import enTeam from './locales/en-US/team.json'

export const I18N_DEFAULT_NAMESPACE = 'common'

const resources = {
  'zh-CN': {
    common: zhCommon,
    navigation: zhNavigation,
    actions: zhActions,
    status: zhStatus,
    contentProjects: zhContentProjects,
    imageAssets: zhImageAssets,
    canvas: zhCanvas,
    validation: zhValidation,
    website: zhWebsite,
    platformAdmin: zhPlatformAdmin,
    autoProcess: zhAutoProcess,
    accountEntry: zhAccountEntry,
    membership: zhMembership,
    legal: zhLegal,
    localPublisher: zhLocalPublisher,
    video: zhVideo,
    overview: zhOverview,
    aiChat: zhAiChat,
    virtualIp: zhVirtualIp,
    imageProcessing: zhImageProcessing,
    competitiveAnalysis: zhCompetitiveAnalysis,
    publishing: zhPublishing,
    marketingAnalytics: zhMarketingAnalytics,
    textAssets: zhTextAssets,
    audioAssets: zhAudioAssets,
    team: zhTeam,
  },
  'en-US': {
    common: enCommon,
    navigation: enNavigation,
    actions: enActions,
    status: enStatus,
    contentProjects: enContentProjects,
    imageAssets: enImageAssets,
    canvas: enCanvas,
    validation: enValidation,
    website: enWebsite,
    platformAdmin: enPlatformAdmin,
    autoProcess: enAutoProcess,
    accountEntry: enAccountEntry,
    membership: enMembership,
    legal: enLegal,
    localPublisher: enLocalPublisher,
    video: enVideo,
    overview: enOverview,
    aiChat: enAiChat,
    virtualIp: enVirtualIp,
    imageProcessing: enImageProcessing,
    competitiveAnalysis: enCompetitiveAnalysis,
    publishing: enPublishing,
    marketingAnalytics: enMarketingAnalytics,
    textAssets: enTextAssets,
    audioAssets: enAudioAssets,
    team: enTeam,
  },
} as const

const isDev = Boolean(import.meta.env?.DEV)

const { locale: initialLocale } = resolveInitialUiLocale()

let didInit = false

/** Idempotent — safe to import from multiple entry points (main.tsx +
 * tests) without double-registering resources. */
export function ensureI18nInitialized(): typeof i18next {
  if (didInit) return i18next
  didInit = true

  void i18next
    .use(initReactI18next)
    .init({
      resources,
      lng: initialLocale,
      fallbackLng: FALLBACK_UI_LOCALE,
      supportedLngs: SUPPORTED_UI_LOCALES as unknown as string[],
      ns: Object.keys(resources[DEFAULT_UI_LOCALE]),
      defaultNS: I18N_DEFAULT_NAMESPACE,
      interpolation: {
        // React already escapes output — double-escaping would corrupt
        // interpolated values like a project title containing `&`/`<`.
        escapeValue: false,
      },
      returnEmptyString: false,
      // Spec §20 — dev-only missing-translation visibility. Never throws,
      // never blocks fallback (spec §19); purely a console signal so an
      // agent/developer doing a batch translation pass can grep for it.
      saveMissing: isDev,
      missingKeyHandler: isDev
        ? (langs, ns, key) => {
            // eslint-disable-next-line no-console
            console.warn(`[i18n] Missing translation key "${ns}:${key}" for locale(s): ${langs.join(', ')}`)
          }
        : undefined,
      debug: false,
      react: {
        useSuspense: false,
      },
    })

  // Dev/E2E-only escape hatch (spec §20/§49 CASE 9) — lets a Playwright
  // spec simulate "en-US is missing a key" without shipping a fake build,
  // by calling `i18next.removeResourceBundle(...)` against the SAME live
  // instance the app renders with. Never attached outside `import.meta.env.DEV`
  // (never present in a Preview/Production bundle).
  if (isDev && typeof window !== 'undefined') {
    ;(window as unknown as { __VISTOERA_I18N__?: typeof i18next }).__VISTOERA_I18N__ = i18next
  }

  return i18next
}

export default i18next
